import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Gauge,
  ChatCircleText,
  Trophy,
  CreditCard,
  ChartLineUp,
  Lightning,
  PencilSimple,
  Notepad,
  Hourglass,
  Bell,
  ShieldCheck,
  ArrowRight,
} from "@phosphor-icons/react";
import MagneticButton from "@/components/MagneticButton";
import Tilt3D from "@/components/Tilt3D";
import RunwayDivider from "@/components/RunwayDivider";
import PilotCarIcon from "@/components/PilotCarIcon";
import SEOHead from "@/components/SEOHead";

const SITE_URL = "https://www.rijschool-autopilot.nl";
const LESKAART_URL = "https://leskaart-autopilot.nl";

const LEVELS = [
  { id: "beginner", label: "Beginner", text: "Eerste stappen. We starten met basisbediening en zithouding." },
  { id: "rookie", label: "Rookie", text: "Je krijgt de auto onder de knie. Schakelen, sturen, stoppen — steeds soepeler." },
  { id: "gevorderd", label: "Gevorderd", text: "Verkeersdeelneming wordt natuurlijk. Je begint zelfstandig te denken." },
  { id: "expert", label: "Expert", text: "Bijzondere verrichtingen en complex verkeer voelen comfortabel." },
  { id: "autopilot", label: "AutoPilot", text: "Examen-klaar. Zelfverzekerd, vooruitziend, ontspannen achter het stuur." },
];

const FEATURES = [
  {
    Icon: Gauge,
    title: "Lessen-teller",
    text: "Zie precies hoeveel lessen je hebt gehad, hoeveel er nog open staan en hoe ver je in je pakket zit. Geen verrassingen — alles transparant.",
  },
  {
    Icon: Bell,
    title: "WhatsApp meldingen",
    text: "Zodra ik een nieuwe les voor je inplan, krijg je automatisch een net bericht met datum, tijd en duur. Geen losse appjes meer kwijtraken.",
  },
  {
    Icon: ChartLineUp,
    title: "Voortgang per categorie",
    text: "Bediening voertuig, verkeersdeelneming en bijzondere verrichtingen — zie per onderdeel hoe je staat en wat we nog gaan oefenen.",
  },
  {
    Icon: Trophy,
    title: "Achievements & level",
    text: "Van Beginner naar AutoPilot. Verdien achievements, zie je level groeien — een leuke extra prikkel om door te knallen.",
  },
  {
    Icon: CreditCard,
    title: "Termijn­betaling in beeld",
    text: "Volgende betaling, openstaand saldo en pakketstatus — altijd inzichtelijk. Zo houden we het samen overzichtelijk.",
  },
  {
    Icon: PencilSimple,
    title: "Zelfreflectie & schetsen",
    text: "Na elke les noteer je wat goed ging en wat lastig was. Schets routes of situaties — wij koppelen er feedback aan.",
  },
  {
    Icon: Hourglass,
    title: "Aftel­timer naar je examen",
    text: "Zodra je examen­datum bekend is, telt je leskaart af tot de grote dag. Zo weet je precies hoeveel dagen je nog hebt om je voor te bereiden.",
  },
  {
    Icon: ChatCircleText,
    title: "Motivatie & feedback",
    text: "Bij elke voortgangssprong krijg je een passend motivatieberichtje. Persoonlijke feedback van mij, direct in je leskaart.",
  },
];

const LeskaartPage = () => {
  return (
    <>
      <SEOHead
        title="Digitale Leskaart — Volg je voortgang live | Rijschool AutoPilot"
        description="Bij Rijschool AutoPilot krijg je een eigen digitale leskaart: lessenteller, examen-teller, achievements, levelsysteem (Beginner → AutoPilot), WhatsApp-meldingen bij elke geplande les en inzicht in je termijnbetaling. Drive like a pilot!"
        keywords="digitale leskaart rijschool, online leskaart eindhoven, rijschool app helmond, leerlingvolgsysteem rijles, rijles voortgang volgen, achievements rijschool, rijschool autopilot leskaart"
        lang="nl"
        canonical={`${SITE_URL}/leskaart`}
        ogLocale="nl_NL"
        alternates={[
          { hreflang: "nl", href: `${SITE_URL}/leskaart` },
          { hreflang: "x-default", href: `${SITE_URL}/leskaart` },
        ]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "Digitale Leskaart — Rijschool AutoPilot",
          applicationCategory: "EducationalApplication",
          operatingSystem: "Web",
          description:
            "Online leerlingvolgsysteem voor leerlingen van Rijschool AutoPilot Eindhoven & Helmond. Voortgang, achievements, lessen- en examenteller, levelsysteem en WhatsApp-meldingen.",
          url: LESKAART_URL,
          provider: { "@type": "DrivingSchool", name: "Rijschool AutoPilot", url: SITE_URL },
        }}
      />

      {/* Hero */}
      <section
        className="relative pt-40 pb-12 px-6 md:px-12 overflow-hidden noise"
        data-testid="leskaart-banner"
      >
        <div className="absolute inset-0 -z-0">
          <div className="absolute -top-32 -right-20 w-[520px] h-[520px] rounded-full bg-[rgba(212,175,55,0.08)] blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[380px] h-[380px] rounded-full bg-[rgba(212,175,55,0.05)] blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-4">
              <span className="runway-stripes w-16 block" />
              <span className="overline">Digitale Leskaart</span>
            </div>
            <h1
              className="text-5xl md:text-6xl lg:text-7xl tracking-tight font-light text-white leading-[1.05]"
              data-testid="leskaart-title"
            >
              Jouw rijbewijs,
              <br />
              <span className="font-display-italic text-gold text-6xl md:text-7xl lg:text-8xl">
                jouw cockpit.
              </span>
            </h1>
            <p className="text-lg text-silver mt-6 max-w-2xl leading-relaxed">
              Bij Rijschool AutoPilot krijg je niet alleen rijlessen — je krijgt
              ook je eigen <span className="text-white">digitale leskaart</span>.
              Een persoonlijk dashboard waarin je live je voortgang volgt,
              automatisch je lesplanning binnenkrijgt en precies ziet welk level
              je hebt bereikt. Transparant, motiverend en altijd binnen handbereik.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <MagneticButton>
                <a
                  href={LESKAART_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-gold"
                  data-testid="leskaart-cta-login"
                >
                  Open mijn leskaart
                  <ArrowRight size={18} weight="bold" className="anim-arrow" />
                </a>
              </MagneticButton>
              <MagneticButton>
                <Link
                  to="/contact"
                  className="btn-ghost"
                  data-testid="leskaart-cta-info"
                >
                  Meer info aanvragen
                </Link>
              </MagneticButton>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 relative"
          >
            <div className="absolute -inset-6 bg-gradient-to-br from-[rgba(212,175,55,0.22)] to-transparent blur-3xl" />
            <div className="relative card-highlight scan-line p-7 overflow-hidden">
              {/* Mock dashboard header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="overline">Leskaart</div>
                  <div className="text-white text-lg tracking-tight font-medium mt-1">
                    Voortgang
                  </div>
                </div>
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-gold">
                  <span className="w-2 h-2 rounded-full bg-gold pulse-dot" />
                  Live
                </div>
              </div>

              {/* Big progress number */}
              <div className="flex items-end gap-4 mb-7">
                <div className="text-6xl font-extralight text-white leading-none">62<span className="text-3xl text-silver/70">%</span></div>
                <div className="text-xs text-silver/80 mb-1">
                  14 van de 25<br />lessen gebruikt
                </div>
              </div>

              {/* Level pill row */}
              <div className="flex items-center gap-2 mb-6">
                {LEVELS.map((l, i) => (
                  <div
                    key={l.id}
                    className={`flex-1 h-1 rounded-full ${
                      i <= 2 ? "bg-gold" : "bg-white/12"
                    }`}
                  />
                ))}
              </div>
              <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-silver/70 mb-7">
                <span>Beginner</span>
                <span className="text-gold">Gevorderd</span>
                <span>AutoPilot</span>
              </div>

              {/* 3 category bars */}
              <div className="space-y-4">
                {[
                  { label: "Bediening voertuig", val: 100 },
                  { label: "Verkeersdeelneming", val: 42 },
                  { label: "Bijzondere verrichtingen", val: 63 },
                ].map((c) => (
                  <div key={c.label}>
                    <div className="flex justify-between text-xs mb-1.5">
                      <span className="text-silver">{c.label}</span>
                      <span className="text-white">{c.val}%</span>
                    </div>
                    <div className="h-1.5 bg-white/8 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${c.val}%`,
                          background:
                            "linear-gradient(90deg, rgba(224,192,104,0.7), #d4af37)",
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer chip */}
              <div className="mt-7 pt-5 border-t border-white/10 flex items-center justify-between text-xs">
                <span className="text-silver/80">Volgende les</span>
                <span className="text-gold">Vrijdag · 13:00</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <RunwayDivider />

      {/* Why it matters */}
      <section className="py-16 lg:py-24 px-6 md:px-12" data-testid="leskaart-why">
        <div className="max-w-5xl mx-auto text-center">
          <div className="overline mb-4">Waarom een digitale leskaart</div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl tracking-tight font-light text-white">
            Eerlijk, helder, en altijd{" "}
            <span className="font-display-italic text-gold text-5xl md:text-6xl lg:text-7xl">
              binnen handbereik.
            </span>
          </h2>
          <p className="text-lg text-silver mt-6 leading-relaxed">
            Rijles is meer dan alleen achter het stuur zitten. Het is een traject.
            Met de digitale leskaart maken we dat traject zichtbaar — voor jou
            én voor mij. Geen geheime scoreboekjes, geen "we zien wel". Je weet
            altijd waar je staat, wat je volgende stap is, en hoe dichtbij dat
            rijbewijs is.
          </p>
        </div>
      </section>

      {/* Feature grid */}
      <section
        className="py-16 lg:py-20 px-6 md:px-12 bg-secondary"
        data-testid="leskaart-features"
      >
        <div className="max-w-7xl mx-auto">
          <div className="mb-12 max-w-2xl">
            <div className="overline mb-4">Wat kun je ermee</div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl tracking-tight font-light text-white">
              Alles wat je nodig hebt,
              <br />
              <span className="font-display-italic text-gold text-4xl md:text-5xl lg:text-6xl">
                op één plek.
              </span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {FEATURES.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
              >
                <Tilt3D max={5} scale={1.01} className="h-full">
                  <div
                    className="card p-6 h-full flex flex-col gap-3"
                    style={{ transformStyle: "preserve-3d" }}
                    data-testid={`leskaart-feature-${i}`}
                  >
                    <f.Icon size={26} weight="duotone" className="text-gold" />
                    <h3 className="text-lg text-white tracking-tight">{f.title}</h3>
                    <p className="text-sm text-silver leading-relaxed">{f.text}</p>
                  </div>
                </Tilt3D>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <RunwayDivider />

      {/* WhatsApp planning preview */}
      <section className="py-20 px-6 md:px-12" data-testid="leskaart-whatsapp">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 order-2 lg:order-1"
          >
            <div className="relative max-w-sm mx-auto">
              <div className="absolute -inset-4 bg-gradient-to-br from-[rgba(37,211,102,0.18)] to-transparent blur-3xl" />
              {/* Stylised chat bubble mockup */}
              <div className="relative card p-6 overflow-hidden">
                <div className="flex items-center gap-3 mb-5 pb-4 border-b border-white/8">
                  <div
                    className="w-9 h-9 rounded-full grid place-items-center"
                    style={{
                      background: "linear-gradient(135deg, #25D366, #128C7E)",
                    }}
                    aria-hidden="true"
                  >
                    <ChatCircleText size={18} weight="fill" className="text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="text-white text-sm font-medium">Rijschool AutoPilot</div>
                    <div className="text-[11px] text-silver/60">via WhatsApp</div>
                  </div>
                </div>

                <div
                  className="relative p-4 rounded-md"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(37,211,102,0.16), rgba(37,211,102,0.08))",
                    border: "1px solid rgba(37,211,102,0.25)",
                  }}
                >
                  <div className="text-white text-sm leading-relaxed mb-3">
                    Hoi <span className="text-gold">Sara</span>! 🚗
                    <br />
                    Je rijles bij Rijschool AutoPilot is ingepland:
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-silver">
                      <span className="text-base">📅</span>
                      <span>
                        Datum: <span className="text-white">vr 17 mei</span>
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-silver">
                      <span className="text-base">⏰</span>
                      <span>
                        Tijd: <span className="text-white">13:00 – 14:30</span>
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-silver">
                      <span className="text-base">⏱️</span>
                      <span>
                        Duur: <span className="text-white">90 minuten</span>
                      </span>
                    </div>
                  </div>

                  <div className="mt-4 pt-3 border-t border-white/10 text-[12px] text-silver/80 leading-relaxed">
                    Bekijk je voortgang:
                    <br />
                    <span className="text-gold">leskaart-autopilot.nl</span>
                  </div>

                  <div className="text-[11px] text-silver/60 text-right mt-3">
                    15:38 ✓✓
                  </div>
                </div>

                <div className="mt-5 text-[11px] uppercase tracking-[0.2em] text-silver/50 text-center">
                  Voorbeeld­bericht
                </div>
              </div>
            </div>
          </motion.div>

          <div className="lg:col-span-7 order-1 lg:order-2">
            <div className="overline mb-4 flex items-center gap-2">
              <Bell size={14} weight="duotone" /> Live planning
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl tracking-tight font-light text-white mb-6">
              Elke geplande les{" "}
              <span className="font-display-italic text-gold text-4xl md:text-5xl lg:text-6xl">
                direct in je WhatsApp.
              </span>
            </h2>
            <p className="text-silver leading-relaxed mb-6 text-lg">
              Zodra ik een nieuwe les voor je inplan, krijg je automatisch een
              net WhatsApp-bericht met datum, tijd en duur — en een directe
              link naar je leskaart. Geen losse berichtjes meer die je kwijtraakt
              en geen onduidelijkheid over de afspraak.
            </p>
            <ul className="space-y-3 text-sm text-silver">
              {[
                "Automatisch bericht zodra ik een les voor je plan",
                "Datum, tijd en duur in één oogopslag",
                "Directe link naar leskaart-autopilot.nl",
                "Verzuim­regels netjes meegestuurd zodat alles helder is",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <ShieldCheck
                    size={18}
                    weight="duotone"
                    className="text-gold flex-shrink-0 mt-0.5"
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <RunwayDivider />

      {/* Levels — Beginner → AutoPilot */}
      <section
        className="py-20 px-6 md:px-12 bg-secondary"
        data-testid="leskaart-levels"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14 max-w-3xl mx-auto">
            <div className="overline mb-4 flex items-center justify-center gap-2">
              <Lightning size={14} weight="fill" /> Levelsysteem
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl tracking-tight font-light text-white">
              Van eerste les tot{" "}
              <span className="font-display-italic text-gold text-4xl md:text-5xl lg:text-6xl">
                AutoPilot.
              </span>
            </h2>
            <p className="text-silver mt-5 leading-relaxed">
              Je begint als Beginner en groeit per les een stukje verder. Elke
              keer dat je voldoende voortgang hebt geboekt op alle onderdelen,
              ga je een level omhoog. Een leuke stip aan de horizon — zonder
              dat het een spelletje wordt.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 relative">
            {/* Connector line (desktop) */}
            <div
              className="hidden lg:block absolute left-0 right-0 top-[58px] h-px pointer-events-none"
              style={{
                background:
                  "linear-gradient(90deg, transparent, rgba(224,192,104,0.45) 15%, rgba(224,192,104,0.45) 85%, transparent)",
              }}
              aria-hidden="true"
            />

            {LEVELS.map((lvl, i) => {
              const isPilot = lvl.id === "autopilot";
              return (
                <motion.div
                  key={lvl.id}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className={`relative card p-5 flex flex-col items-center text-center ${
                    isPilot ? "card-highlight" : ""
                  }`}
                  data-testid={`level-card-${lvl.id}`}
                >
                  <div
                    className={`relative w-20 h-20 rounded-full grid place-items-center mb-4 ${
                      isPilot ? "" : ""
                    }`}
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(224,192,104,0.18), rgba(224,192,104,0.03))",
                      border: "1px solid rgba(224,192,104,0.55)",
                      boxShadow: isPilot
                        ? "0 0 26px rgba(224,192,104,0.45)"
                        : "0 0 14px rgba(224,192,104,0.12)",
                    }}
                  >
                    <span className="text-gold font-display-italic text-2xl leading-none">
                      {i + 1}
                    </span>
                    {isPilot && (
                      <span className="absolute -bottom-2 left-1/2 -translate-x-1/2">
                        <PilotCarIcon size={26} weight="fill" className="text-gold anim-salute" />
                      </span>
                    )}
                  </div>
                  <div className="text-white text-lg tracking-tight font-medium">
                    {lvl.label}
                  </div>
                  <div className="text-xs text-silver/80 leading-relaxed mt-2">
                    {lvl.text}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-6 md:px-12" data-testid="leskaart-cta">
        <div className="max-w-4xl mx-auto text-center">
          <div className="overline mb-4">Klaar om te starten?</div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl tracking-tight font-light text-white mb-6">
            Eerste les =
            <br />
            <span className="font-display-italic text-gold text-5xl md:text-6xl lg:text-7xl">
              eerste pagina van jouw leskaart.
            </span>
          </h2>
          <p className="text-silver leading-relaxed mb-10 text-lg max-w-2xl mx-auto">
            Word je leerling bij AutoPilot? Dan krijg je direct toegang tot je
            persoonlijke digitale leskaart. Vanaf les één zie je je voortgang,
            je level en je achievements groeien. Drive like a pilot.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4">
            <MagneticButton>
              <Link to="/contact" className="btn-gold" data-testid="leskaart-cta-trial">
                Plan een proefles
                <ArrowRight size={18} weight="bold" className="anim-arrow" />
              </Link>
            </MagneticButton>
            <MagneticButton>
              <a
                href={LESKAART_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost"
                data-testid="leskaart-cta-open"
              >
                Open leskaart-autopilot.nl
              </a>
            </MagneticButton>
          </div>
        </div>
      </section>
    </>
  );
};

export default LeskaartPage;
